import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { BookOpenIcon } from 'lucide-react';
/**
 * DragDropFillRenderer — Fill blanks by selecting a word and a target blank.
 *
 * Supports two seed data formats:
 * Format A (canonical): template with ___, blanks[{id, correctAnswer}], dragOptions[]
 * Format B (legacy):    template with {0}/{1}, draggables[], correctPlacements{}
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 */
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { DRAG_DROP_FILL_MODES, readMode } from '../../../content/questions/questionModes';
import { hapticManager } from '../../../utils/hapticManager';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
interface NormalizedBlank {
  id: string;
  correctAnswer: string;
}
function normalizeConfig(config: Record<string, unknown>): {
  parts: string[];
  blanks: NormalizedBlank[];
  dragOptions: string[];
} {
  const template: string = config.template as string || '';
  const rawBlanks = config.blanks as NormalizedBlank[] | undefined;
  const draggables = config.draggables as string[] | undefined;
  const correctPlacements = config.correctPlacements as Record<string, number> | undefined;
  if (draggables && correctPlacements) {
    const placeholderRegex = /\{(\d+)\}/g;
    const ids: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = placeholderRegex.exec(template)) !== null) {
      ids.push(match[1]);
    }
    return {
      parts: template.split(/\{\d+\}/),
      blanks: ids.map(id => ({
        id,
        correctAnswer: draggables[correctPlacements[id] ?? 0] || ''
      })),
      dragOptions: draggables
    };
  }
  return {
    parts: template.split('___'),
    blanks: rawBlanks || [],
    dragOptions: config.dragOptions as string[] || config.options as string[] || []
  };
}
export function DragDropFillRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = (question.data || {}) as Record<string, unknown>;
  const {
    parts,
    blanks,
    dragOptions
  } = normalizeConfig(config);
  const mode = readMode(question, DRAG_DROP_FILL_MODES);
  const isWithBank = mode === 'with_bank';
  const [filledBlanks, setFilledBlanks] = useState<Record<string, string>>({});
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [liveMessage, setLiveMessage] = useState('Kies een woord en daarna een lege plek.');
  useEffect(() => {
    setFilledBlanks({});
    setSelectedOption(null);
    setLiveMessage('Kies een woord en daarna een lege plek.');
  }, [question.id]);
  useEffect(() => {
    const isReady = blanks.every(blank => filledBlanks[blank.id]);
    onReadyChange?.(isReady);
  }, [filledBlanks, blanks, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(filledBlanks), [filledBlanks]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const focusBlank = (index: number) => {
    document.getElementById(`drag-blank-${question.id}-${index}`)?.focus();
  };
  const focusOption = (index: number) => {
    document.getElementById(`drag-option-${question.id}-${index}`)?.focus();
  };
  const handleOptionClick = (option: string) => {
    if (shouldValidate || Object.values(filledBlanks).includes(option)) return;
    const isSelected = selectedOption === option;
    setSelectedOption(isSelected ? null : option);
    setLiveMessage(isSelected ? `${option} niet meer geselecteerd.` : `${option} geselecteerd. Kies nu een lege plek.`);
    hapticManager.hapticSelection();
  };
  const handleBlankClick = (blankId: string, blankIndex: number) => {
    if (shouldValidate) return;
    const existingValue = filledBlanks[blankId];
    if (!selectedOption) {
      if (!existingValue) {
        setLiveMessage('Kies eerst een woord uit de woordbank.');
        return;
      }
      setFilledBlanks(previous => {
        const next = {
          ...previous
        };
        delete next[blankId];
        return next;
      });
      setLiveMessage(`${existingValue} is terug in de woordbank.`);
      hapticManager.hapticLight();
      return;
    }
    setFilledBlanks(previous => ({
      ...previous,
      [blankId]: selectedOption
    }));
    setLiveMessage(existingValue ? `${selectedOption} geplaatst op plek ${blankIndex + 1}. ${existingValue} is weer beschikbaar.` : `${selectedOption} geplaatst op plek ${blankIndex + 1}.`);
    setSelectedOption(null);
    hapticManager.hapticSelection();
  };
  const handleBlankKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBlank((index + 1) % blanks.length);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBlank((index - 1 + blanks.length) % blanks.length);
    } else if (event.key === 'ArrowDown' && dragOptions.length > 0) {
      event.preventDefault();
      focusOption(0);
    }
  };
  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusOption((index + 1) % dragOptions.length);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusOption((index - 1 + dragOptions.length) % dragOptions.length);
    } else if (event.key === 'ArrowUp' && blanks.length > 0) {
      event.preventDefault();
      focusBlank(0);
    }
  };
  return <div className="w-full max-w-2xl mx-auto flex flex-col gap-5 sm:gap-7" data-id="element-12056" data-mp="1micrnymsg1jg">
      <p className="text-center text-sm text-slate-600" data-id="element-12057" data-mp="1mga4rngxmoiy">
        Kies een woord en tik daarna op de plek waar het hoort. Tik op een
        ingevuld vakje zonder gekozen woord om het woord terug te leggen.
      </p>

      <div className="p-4 sm:p-6 bg-indigo-50 rounded-2xl border-2 border-indigo-100 text-base sm:text-xl leading-relaxed sm:leading-loose flex flex-wrap items-center gap-1.5 sm:gap-2 justify-center" aria-label="Zin met invulplekken" data-id="element-12058" data-mp="3obkfxxkdpthh">
        
        {parts.map((part, index) => <Fragment key={index} data-id="element-12059" data-mp="1s6uyn5lwgifx" data-mpi={index}>
            <span data-id="element-12060" data-mp="1w86h8r6cw3em">
              <LatexRenderer content={part} data-id="element-12061" data-mp="1w7oinnw81d3j" />
            </span>
            {index < parts.length - 1 && blanks[index] && (() => {
          const blank = blanks[index];
          const value = filledBlanks[blank.id];
          const isTarget = selectedOption !== null;
          return <button id={`drag-blank-${question.id}-${index}`} type="button" onClick={() => handleBlankClick(blank.id, index)} onKeyDown={event => handleBlankKeyDown(event, index)} disabled={shouldValidate} aria-label={`Plek ${index + 1}${value ? `, gevuld met ${value}` : ', leeg'}${isTarget ? `, plaats ${selectedOption} hier` : ''}`} className={`min-w-[80px] sm:min-w-[100px] min-h-[44px] h-11 px-3 rounded-lg border-2 border-dashed transition-all flex items-center justify-center font-bold text-sm sm:text-base touch-manipulation focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:ring-offset-2 disabled:cursor-default ${value ? 'bg-white border-indigo-500 text-indigo-700 shadow-sm' : 'bg-white/50 border-slate-300 text-slate-400'} ${isTarget ? 'ring-2 ring-indigo-300 border-indigo-400 bg-white' : ''}`} data-id="element-12062" data-mp="2b5gxedty1xgo">
                
                    {value || '?'}
                  </button>;
        })()}
          </Fragment>)}
      </div>

      {isWithBank && <div className="flex items-center gap-2 -mb-2 px-1" data-id="element-12063" data-mp="3mpv823jlaaod">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-amber-100 text-amber-700" aria-hidden="true" data-id="element-12064" data-mp="3hqs2mhzp62fv">
          
            <BookOpenIcon className="w-3.5 h-3.5" strokeWidth={2.25} data-id="element-12065" data-mp="3cyqtv61qmtul" />
          </span>
          <span className="text-sm font-semibold text-slate-700" data-id="element-12066" data-mp="38ygl5lmk9pc1">
            Woordbank
          </span>
        </div>}

      <div className={isWithBank ? 'flex flex-wrap gap-2.5 sm:gap-3 justify-center px-3 py-4 sm:py-5 bg-amber-50/60 border-2 border-amber-200 border-dashed rounded-2xl' : 'flex flex-wrap gap-2.5 sm:gap-3 justify-center px-1'} role="listbox" aria-label={isWithBank ? 'Woordbank. Kies een woord om te plaatsen.' : 'Kies een woord om te plaatsen.'} data-id="element-12067" data-mp="9dl30te5citp">
        
        {dragOptions.map((option, index) => {
        const isPlaced = Object.values(filledBlanks).includes(option);
        const isSelected = selectedOption === option;
        return <button key={`${option}-${index}`} id={`drag-option-${question.id}-${index}`} type="button" role="option" aria-selected={isSelected} aria-disabled={isPlaced} onClick={() => handleOptionClick(option)} onKeyDown={event => handleOptionKeyDown(event, index)} disabled={isPlaced || shouldValidate} className={`px-3.5 sm:px-4 py-2.5 min-h-[44px] rounded-xl border-2 font-medium text-base sm:text-lg transition-all touch-manipulation focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:ring-offset-2 ${isPlaced ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-default line-through' : isSelected ? 'bg-indigo-600 border-indigo-700 text-white shadow-md' : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-400 hover:shadow-md active:scale-95 sm:hover:-translate-y-0.5'}`} data-id="element-12068" data-mp="16yqojcl63gm0" data-mpi={index}>
              
              <LatexRenderer content={option} data-id="element-12069" data-mp="2dtr4v3y2p6cp" />
              <span className="sr-only" data-id="element-12070" data-mp="2drdbczj1k279">
                {isPlaced ? ' geplaatst' : isSelected ? ' geselecteerd' : ''}
              </span>
            </button>;
      })}
      </div>

      <p className="text-center text-slate-500 text-xs sm:text-sm" aria-live="polite" data-id="element-12071" data-mp="1m2tjkky1co19">
        
        {liveMessage}
      </p>
    </div>;
}